import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { t } from "ttag";
import { Button, ChevronLeft, IconButton, Select } from "@coolremote/shared";
import { isQaBypassCredentials } from "../../../models/devicePublicRegistrationTestBypass";
import { ExistingUserFlowState, isLoggedIn } from "./existingUserFlowState";
import { CustomerSummary, listCustomers } from "./existingUserFlowApi";
import parentStyles from "../DeviceRegistrationPublic.module.css";
import styles from "./ExistingUserFlow.module.css";

const CREATE_NEW_CUSTOMER_VALUE = "__create_new_customer__";

/**
 * Step 2 of the existing-user branch: pick one of the logged-in
 * user's customers, or create one. `Customer.getMyCustomers` is off-limits to
 * a Control-only account (no Professional access), so those users skip this
 * screen entirely and go straight to "+ Create new customer" instead —
 * creating a customer from a device works for any account type, Professional
 * access is only required to list existing customers/sites.
 */
const SelectCustomerStep: React.FC = () => {
  const history = useHistory<ExistingUserFlowState | undefined>();
  const deviceState = history.location.state;
  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState(deviceState?.customerId ?? "");

  useEffect(() => {
    if (!isLoggedIn(deviceState)) {
      history.replace("/register-device/existing-user");
      return;
    }
    if (deviceState.hasProfessionalAccess === false) {
      history.replace("/register-device/existing-user/customer-name", deviceState);
      return;
    }
    let active = true;
    listCustomers().then((result) => {
      if (active) {
        setCustomers(result);
        setIsLoadingCustomers(false);
        // QA bypass — pre-select the first customer so the wizard can be
        // clicked through end-to-end without a real choice to make.
        if (!deviceState.customerId && isQaBypassCredentials(deviceState.serial, deviceState.pin) && result[0]) {
          setSelectedCustomerId(result[0].customerId);
        }
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isLoggedIn(deviceState) || deviceState.hasProfessionalAccess === false) {
    return null;
  }

  const handleBack = () => {
    history.goBack();
  };

  // A just-created customer (from NewCustomerStep) may not be in `listCustomers`'s
  // response yet — merge it in so the trigger can actually display it as selected.
  const knownCustomers =
    deviceState.customerId &&
    deviceState.customerName &&
    !customers.some((customer) => customer.customerId === deviceState.customerId)
      ? [{ customerId: deviceState.customerId, name: deviceState.customerName }, ...customers]
      : customers;

  const handleSelectCustomer = (customerId: string) => {
    if (customerId === CREATE_NEW_CUSTOMER_VALUE) {
      history.push("/register-device/existing-user/customer-name", deviceState);
      return;
    }
    setSelectedCustomerId(customerId);
  };

  const handleNext = () => {
    const customer = knownCustomers.find((candidate) => candidate.customerId === selectedCustomerId);
    if (!customer) {
      return;
    }
    const nextState = {
      ...deviceState,
      customerId: customer.customerId,
      customerName: customer.name
    } as ExistingUserFlowState;
    // Attach the chosen customer to *this* history entry too, so that going
    // back from Select Site lands here with the choice restored.
    history.replace(history.location.pathname, nextState);
    history.push("/register-device/existing-user/select-site", nextState);
  };

  const customerOptions = [
    ...knownCustomers.map((customer) => ({ value: customer.customerId, label: customer.name })),
    { value: CREATE_NEW_CUSTOMER_VALUE, label: t`+ Create new customer` }
  ];

  return (
    <div className={parentStyles.screen}>
      <div className={parentStyles.appBar}>
        <IconButton className={parentStyles.backButton} onClick={handleBack} aria-label={t`Back`}>
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={parentStyles.appBarTitle}>{t`Select Customer`}</span>
        <span />
      </div>

      <div className={styles.stepContent}>
        <p className={styles.stepSubtitle}>{t`Choose the customer for this device:`}</p>
        <Select
          fullWidth
          searchable
          menuPortal
          maxMenuWidth="calc(100vw - 48px)"
          disabled={isLoadingCustomers}
          value={selectedCustomerId}
          onChange={handleSelectCustomer}
          options={customerOptions}
        />
        <Button
          variant="contained"
          className={styles.primaryButton}
          disabled={!selectedCustomerId}
          onClick={handleNext}
        >
          {t`Next`}
        </Button>
      </div>
    </div>
  );
};

export default SelectCustomerStep;
